import {
    useCallback,
    useEffect,
    useMemo, useState,
} from 'react'
import {
    useJsApiLoader, Libraries,
} from '@react-google-maps/api'

import {
    Library,
} from 'app/types/googlemaps.types'
import StatusHandler from 'app/shared-components/StatusHandler'
import FormWithControls from 'app/shared-components/FormWithControls'
import requests from 'app/requests/BillingCompany.requests'
import useContactManagementRoutes from 'app/SkyNetRoutes/hooks/useContactManagementRoutes'
import Button from 'app/shared-components/Button'
import ConfirmationDialog from 'app/shared-components/ConfirmationDialog'

import useGetBillingCompany from '../hooks/useGetBillingCompany'
import {
    BillingCompany,
} from '../BillingCompanyForm.types'
import useArchiveBillingCompany from '../hooks/useArchiveBillingCompany'
import useRestoreBillingCompany from '../hooks/useRestoreBillingCompany'
import getFields from './billingCompanyDataUpdate.fields'

const libraries: Libraries = [Library.PLACES]
const {
    REACT_APP_GOOGLE_MAP_KEY: googleMapKey,
} = process.env

const ACTIONS = {
    ARCHIVE: 'ARCHIVE',
    RESTORE: 'RESTORE',
}

const DIALOG_TITLE_MAP = {
    ARCHIVE: 'ARCHIVING',
    RESTORE: 'RESTORING',
}

const DIALOG_CONTENT_MAP = {
    ARCHIVE: 'archive',
    RESTORE: 'restore',
}

const BillingCompanyDataUpdate = ({
    id,
}: { id: number }) => {
    const {
        isLoaded: isGoogleMapsServiceLoaded,
    } = useJsApiLoader({
        googleMapsApiKey: googleMapKey,
        libraries,
    })

    const ContactManagementRoutes = useContactManagementRoutes()

    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
        invalidate,
    } = useGetBillingCompany(id)

    const [
        value,
        setValue,
    ] = useState<BillingCompany>()

    useEffect(() => {
        setValue(data)
    }, [data])

    const setAutocomplete = useCallback((autofillValues, isSetOwnField = false) => {
        const updatedValue = isSetOwnField
            ? {
                ...value,
                additional: autofillValues,
            }
            : {
                ...value,
                ...autofillValues,
            }

        if (JSON.stringify(updatedValue) !== JSON.stringify(value)) {
            setValue(updatedValue)
        }
    }, [value])

    const [
        actionDialog,
        setActionDialog,
    ] = useState({
        open: false,
        action: undefined,
    })

    const onSuccessArchiveRestoreLocal = useCallback(() => {
        ContactManagementRoutes.BillingCompanies.navigate()
    }, [ContactManagementRoutes])

    const {
        mutate: mutateArchiveBillingCompany,
    } = useArchiveBillingCompany({
        onSuccess: onSuccessArchiveRestoreLocal,
    })

    const {
        mutate: mutateRestoreBillingCompany,
    } = useRestoreBillingCompany({
        onSuccess: onSuccessArchiveRestoreLocal,
    })

    const onArchive = useCallback(() => {
        setActionDialog({
            open: true,
            action: ACTIONS.ARCHIVE,
        })
    }, [])
    const onRestore = useCallback(() => {
        setActionDialog({
            open: true,
            action: ACTIONS.RESTORE,
        })
    }, [])

    const customButtons = useMemo(() => {
        return data?.archived ? [<Button
            label="Restore"
            secondary
            key="archive"
            onClick={onRestore}
        />] : [<Button
            label="Archive"
            secondary
            key="archive"
            onClick={onArchive}
        />]
    }, [
        data?.archived,
        onArchive,
        onRestore,
    ])

    const fields = useMemo(() => {
        return getFields({
            setAutocomplete,
            currentPlaceId: value?.currentPlaceId,
            value,
            isGoogleMapsServiceLoaded,
        })
    }, [
        isGoogleMapsServiceLoaded,
        setAutocomplete,
        value,
    ])

    const handleConfirmAction = useCallback((confirm) => {
        if (confirm) {
            if (actionDialog.action === ACTIONS.ARCHIVE) {
                mutateArchiveBillingCompany({
                    id: data?.id,
                })
            } else {
                mutateRestoreBillingCompany({
                    id: data?.id,
                })
            }
        }

        setActionDialog({
            open: false,
            action: undefined,
        })
    }, [
        actionDialog.action,
        data?.id,
        mutateArchiveBillingCompany,
        mutateRestoreBillingCompany,
    ])

    return (
        <StatusHandler
            isSuccess={isDataReady}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <>
                <FormWithControls
                    name="DataUpdateTab"
                    value={value}
                    setExternalValue={setValue}
                    onSuccess={invalidate}
                    fields={fields}
                    requestParams={requests.update({
                        id,
                    })}
                    customButtons={customButtons}
                    exists
                />
                <ConfirmationDialog
                    open={actionDialog.open}
                    handleClose={handleConfirmAction}
                    dialogTitle={`BILLING COMPANY ${DIALOG_TITLE_MAP[actionDialog.action]} CONFIRMATION`}
                    dialogContent={`Do you want to ${DIALOG_CONTENT_MAP[actionDialog.action]} this billing company?`}
                />
            </>
        </StatusHandler>
    )
}

export default BillingCompanyDataUpdate
